import { useQuery, useQueryClient, InfiniteData } from "@tanstack/react-query";
import {
  UseAPIOptions,
  QueryKeyArray,
  // Plain async fetch helper (not a hook despite the name) — alias so the
  // rules-of-hooks lint doesn't flag the calls inside the two queryFn
  // callbacks below.
  useAPIHelper as fetchAPIData,
  useResetAPI,
  createAuthRetryHandler
} from "@/services/api/hook";
import {
  nextPageNumber,
  useInfiniteFilterQuery
} from "@/services/api/useInfiniteFilterQuery";
import { useAuth } from "@/services/auth/AuthContext";
import { useEffect, useMemo, useCallback, useRef } from "react";
import { useDebounce } from "use-debounce";
import { isEqual } from "lodash";
import {
  generateCacheKey,
  saveToViewCache,
  loadFromViewCache
} from "@/services/cache/viewCache";
import { collapseNoValueFilters } from "@/services/api/noValueFilters";
import {
  Filters,
  EmptyFilters,
  SearchQuery,
  EntityData,
  EntityPage,
  QueryData,
  QueryAggregations,
  ComputedFields
} from "@/services/api/types";
import { MIN_CHARACTERS_FOR_SEARCH } from "@/services/store/BoundStore";

interface UseFilteredEntitiesInfiniteOptions<
  T extends EntityData,
  F extends Filters
> {
  filters?: F;
  search?: SearchQuery;
  groupedBy?: keyof T;
  /**
   * SQL grouping function applied to `groupedBy` — "quarter", "month" or "ytd".
   * Lets a table group on a value derived from a date column (a closing
   * quarter) rather than only on the column itself.
   */
  groupedByFunction?: string;
  pageSize?: number;
  sortField?: string;
  sortAsc?: boolean;
  fetchAll?: boolean;
  retryOnError?: boolean;
  enabled?: boolean;
  alwaysStale?: boolean;
  /**
   * Refetch on mount a list that was invalidated while nothing displayed it,
   * so a refresh can skip the lists off screen instead of fetching them all.
   */
  refetchInvalidatedOnMount?: boolean;
  computed_fields?: ComputedFields;
  minSearchCharacters?: number;
  /**
   * Extra fields merged into the **aggregations** request body, for a read whose
   * endpoint takes more than the filters/search this hook builds — e.g. the
   * Contracts table's Compare-to period, which the backend needs in order to
   * attach the totals bar's as-of figure to the aggregation block.
   *
   * Two consequences worth knowing before reaching for this:
   *
   * - These never ride the paginated rows request, so changing one does **not**
   *   restart row pagination; only the aggregations query refetches. That is the
   *   point — the aggregation block is the only part of the response they shape,
   *   and sending them with the rows would repeat the server's work on every
   *   page of a scroll for a figure nobody reads.
   * - They are only sent when the aggregations query actually fires, which it
   *   does not when `groupedBy` is unset *and* the factory's `defaultAggregations`
   *   is empty. A caller that passes extras without either would see them
   *   silently dropped, so pair them with one.
   *
   * Pass a memoized object; a fresh literal each render refetches every render.
   *
   * Declare the shape at the call site rather than passing a bare object: these
   * keys go over the wire, and the endpoints behind this reader are CamelModels
   * that silently *drop* a key they do not recognize instead of rejecting it —
   * so a mis-cased field would compile, pass CI, and ship a feature that never
   * arrives. A named type makes that a compile error where it is written.
   */
  extraBodyParams?: ExtraBodyParams;
}

/**
 * The body keys this reader builds itself, which a caller's extras may not set.
 *
 * `never` rather than merely being overwritten: the aggregations query below
 * spreads the extras first, so a collision is already harmless at runtime, but
 * silently ignoring a field the caller asked for is its own bug. This turns it
 * into a compile error at the call site.
 */
export type ExtraBodyParams = Record<string, unknown> & {
  filters?: never;
  aggregations?: never;
  search?: never;
  sort?: never;
};

// Both queries persist their fresh response the same way, so the
// stale-while-revalidate write-back lives in one place.
const useViewCacheWriteBack = (
  cacheKey: string | null,
  tenantId: string | null | undefined,
  data: unknown,
  isPlaceholderData: boolean
) => {
  const previousDataRef = useRef(data);
  useEffect(() => {
    if (
      cacheKey &&
      tenantId &&
      data &&
      !isPlaceholderData &&
      data !== previousDataRef.current
    ) {
      previousDataRef.current = data;
      saveToViewCache(cacheKey, data, tenantId);
    }
  }, [cacheKey, tenantId, data, isPlaceholderData]);
};

export const createUseFilteredEntitiesInfinite = <
  T extends EntityData,
  F extends Filters = Filters
>(
  entitiesName: string,
  defaultPageSize: number,
  options?: {
    responseKey?: string | string[];
    extraQueryParams?: Record<string, string>;
    defaultAggregations?: Record<string, unknown>;
  }
) => {
  const responseKeys = (() => {
    const k = options?.responseKey;
    if (!k) return [entitiesName];
    return Array.isArray(k) ? k : [k];
  })();
  const pickEntities = (page: EntityPage<T>): T[] | undefined => {
    for (const key of responseKeys) {
      const value = page[key];
      if (Array.isArray(value)) return value as T[];
    }
    return undefined;
  };
  const extraQueryParams = options?.extraQueryParams;
  const defaultAggregations = options?.defaultAggregations;

  const useResetFilteredEntitiesInfinite = (
    pageName?: string,
    entityId?: string
  ) => useResetAPI(`${entitiesName}/filter`, true, pageName, entityId);

  const useFilteredEntitiesInfinite = (
    useStore: () => {
      addEntity: (entity: T) => void;
    },
    {
      filters = EmptyFilters as F,
      search,
      groupedBy,
      groupedByFunction,
      pageSize = defaultPageSize,
      sortField,
      sortAsc,
      fetchAll = false,
      enabled = true,
      alwaysStale = false,
      refetchInvalidatedOnMount = false,
      computed_fields = ComputedFields.MINIMAL,
      minSearchCharacters = MIN_CHARACTERS_FOR_SEARCH,
      extraBodyParams
    }: UseFilteredEntitiesInfiniteOptions<T, F>,
    pageName?: string,
    entityId?: string
  ) => {
    const { addEntity } = useStore();
    const { isAuthenticated, token, tenantId, tokenExpiry, login } = useAuth();

    // The aggregations query below runs here rather than inside
    // useInfiniteFilterQuery, so it needs its own credentials. Same shape as
    // the hook's: read inside queryFn, written after commit.
    const authRef = useRef({ token, tenantId, tokenExpiry });
    useEffect(() => {
      authRef.current = { token, tenantId, tokenExpiry };
    }, [token, tenantId, tokenExpiry]);
    const queryClient = useQueryClient();

    const queryKeyEndpoint = `${pageName ? `${pageName}/` : ""}${entitiesName}/filter`;

    const queryParams = useMemo(
      () => ({
        filters,
        search
      }),
      [filters, search]
    );
    // Debounce on the VALUE of queryParams, not its reference. `search` is a
    // fresh object literal every render (e.g. `{ term }` rebuilt in
    // useEntitySearch), so without a value comparison any component that
    // re-renders while a search is active resets the timer on every render and
    // the debounced value never settles — the search term never reaches the
    // query key and no request is ever issued (e.g. table entity filters).
    // isEqual (not JSON.stringify) so key order in nested filters doesn't matter.
    const [debouncedParams] = useDebounce(queryParams, 300, {
      equalityFn: isEqual
    });

    const activeSearch = useMemo(
      () =>
        debouncedParams.search &&
        debouncedParams.search.term.length >= minSearchCharacters
          ? debouncedParams.search
          : undefined,
      [debouncedParams.search, minSearchCharacters]
    );

    const collapsedFilters = useMemo(
      () => collapseNoValueFilters(debouncedParams.filters) as F,
      [debouncedParams.filters]
    );

    const queryData: QueryData<T, F> = useMemo(
      () => ({
        filters: collapsedFilters,
        ...(sortField && {
          sort: { field: sortField, ascending: sortAsc }
        }),
        ...(activeSearch && { search: activeSearch })
      }),
      [collapsedFilters, sortField, sortAsc, activeSearch]
    );

    // Aggregations describe the whole filtered set, so they are their own
    // query — one request per filter, not one per page of the scroll. Sorting
    // is left out of it: it cannot change a sum, and including it would throw
    // the numbers away every time the user sorts.
    const aggregationsData: QueryData<T, F> | null = useMemo(() => {
      const aggregations: QueryAggregations<T> = {
        ...(defaultAggregations ?? {})
      };
      if (groupedBy) {
        aggregations.groupBy = {
          field: groupedBy,
          ...(groupedByFunction && { function: groupedByFunction })
        };
      }
      if (Object.keys(aggregations).length === 0) return null;

      // Extras ride the aggregations request, not the paginated one: they exist
      // to shape the aggregation block this query's response carries, and the
      // rows response's aggregations are not read at all. Sending them with the
      // rows would also repeat the server's extra work on every page of a
      // scroll for a figure nobody reads.
      //
      // Spread first so the keys below always win; `ExtraBodyParams` already
      // rejects them, and the ordering is what holds if that type is loosened.
      return {
        ...extraBodyParams,
        filters: collapsedFilters,
        aggregations,
        ...(activeSearch && { search: activeSearch })
      };
      // `defaultAggregations` is deliberately absent: it is a factory-closure
      // value, not a reactive one, and naming it is a lint error.
    }, [
      collapsedFilters,
      activeSearch,
      groupedBy,
      groupedByFunction,
      extraBodyParams
    ]);

    const queryOptions: UseAPIOptions = useMemo(
      () => ({
        method: "POST",
        queryParams: {
          size: pageSize.toString(),
          computed_fields: computed_fields,
          ...extraQueryParams
        },
        pathParams: entityId ? { entityId } : undefined,
        data: queryData
      }),
      [pageSize, computed_fields, entityId, queryData]
    );

    const shouldCache = !alwaysStale && !(pageName && entityId);
    const cacheKey = useMemo(
      () =>
        shouldCache ? generateCacheKey([queryKeyEndpoint, queryOptions]) : null,
      [shouldCache, queryKeyEndpoint, queryOptions]
    );

    const aggregationsQueryOptions: UseAPIOptions | null = useMemo(
      () =>
        aggregationsData
          ? {
              method: "POST",
              queryParams: {
                // The aggregations cover the whole filtered set; the rows come
                // from the paginated query, so this asks for as few as the API
                // will return.
                size: "1",
                page: "1",
                // Only the aggregation block is read off this response, so the
                // per-row computed fields would be built and thrown away.
                computed_fields: ComputedFields.NONE,
                ...extraQueryParams
              },
              pathParams: entityId ? { entityId } : undefined,
              data: aggregationsData
            }
          : null,
      [aggregationsData, entityId]
    );

    // The tenant scopes the request through its header, so it has to scope the
    // cache too - without it a tenant switch under a live QueryClient serves
    // the previous tenant's totals for the same route and body.
    const aggregationsQueryKey: QueryKeyArray = useMemo(
      () => [
        queryKeyEndpoint,
        aggregationsQueryOptions ?? undefined,
        { tenantId }
      ],
      [queryKeyEndpoint, aggregationsQueryOptions, tenantId]
    );

    const aggregationsCacheKey = useMemo(
      () =>
        shouldCache && aggregationsQueryOptions
          ? generateCacheKey(aggregationsQueryKey)
          : null,
      [shouldCache, aggregationsQueryOptions, aggregationsQueryKey]
    );

    const aggregationsResult = useQuery<
      EntityPage<T>,
      Error,
      EntityPage<T>,
      QueryKeyArray
    >({
      queryKey: aggregationsQueryKey,
      queryFn: async ({ signal, queryKey: currentQueryKey }) =>
        await fetchAPIData(authRef.current, currentQueryKey, signal),
      enabled: isAuthenticated && enabled && aggregationsQueryOptions !== null,
      retry: createAuthRetryHandler(login),
      staleTime: alwaysStale ? 0 : 5 * 60 * 1000,
      gcTime: alwaysStale ? 0 : 10 * 60 * 1000,
      refetchOnMount: refetchInvalidatedOnMount
        ? (query) => query.state.isInvalidated
        : false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData:
        aggregationsCacheKey && tenantId
          ? () => {
              const cached = loadFromViewCache(aggregationsCacheKey, tenantId);
              // Only the aggregation block is ever read off this entry, so a
              // cached shape without one has nothing to stand in for.
              if (
                !cached ||
                typeof cached !== "object" ||
                !("aggregations" in cached)
              ) {
                return undefined;
              }
              return cached as EntityPage<T>;
            }
          : undefined
    });

    const result = useInfiniteFilterQuery<EntityPage<T>>({
      routeKey: queryKeyEndpoint,
      requestOptions: queryOptions,
      enabled,
      // The server's own page count decides where the list ends. A short page
      // does not: filtering applied after paging can return fewer rows than
      // asked for with pages still to come, and treating that as the end drops
      // them silently.
      getNextPageParam: nextPageNumber,
      staleTime: alwaysStale ? 0 : 5 * 60 * 1000,
      gcTime: alwaysStale ? 0 : 10 * 60 * 1000,
      refetchOnMount: refetchInvalidatedOnMount
        ? (query) => query.state.isInvalidated
        : false,
      placeholderData:
        cacheKey && tenantId
          ? () => {
              const cached = loadFromViewCache(cacheKey, tenantId);
              if (
                !cached ||
                typeof cached !== "object" ||
                !Array.isArray((cached as { pages?: unknown }).pages)
              ) {
                return undefined;
              }
              return cached as InfiniteData<EntityPage<T>, number>;
            }
          : undefined
    });

    const { queryKey } = result;

    // Store entities in Zustand as they arrive
    useEffect(() => {
      if (result.data?.pages) {
        result.data.pages.forEach((page) => {
          const entities = pickEntities(page);
          if (entities) {
            entities.forEach(addEntity);
          }
        });
      }
    }, [result.data, addEntity]);

    // Persist fresh data to localStorage for stale-while-revalidate
    useViewCacheWriteBack(
      cacheKey,
      tenantId,
      result.data,
      result.isPlaceholderData
    );
    useViewCacheWriteBack(
      aggregationsCacheKey,
      tenantId,
      aggregationsResult.data,
      aggregationsResult.isPlaceholderData
    );

    const entities = useMemo(() => {
      return (
        result.data?.pages.flatMap((page) => pickEntities(page) ?? []) ?? []
      );
    }, [result.data]);

    const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = result;

    // It's possible to load all the entities automatically
    useEffect(() => {
      if (fetchAll && hasNextPage && !isFetchingNextPage && !isError) {
        fetchNextPage();
      }
    }, [fetchAll, hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

    const aggregations = useMemo(
      () => aggregationsResult.data?.aggregations ?? {},
      [aggregationsResult.data]
    );

    // Function to refetch only this specific query with its filters
    const refetchQuery = useCallback(async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({ queryKey: aggregationsQueryKey })
      ]);
    }, [queryClient, queryKey, aggregationsQueryKey]);

    // Function to reset this specific query (clear data and refetch)
    const resetQuery = useCallback(async () => {
      await Promise.all([
        queryClient.resetQueries({ queryKey }),
        queryClient.resetQueries({ queryKey: aggregationsQueryKey })
      ]);
    }, [queryClient, queryKey, aggregationsQueryKey]);

    return {
      entities,
      hasNextPage: result.hasNextPage,
      isFetchingNextPage: result.isFetchingNextPage,
      fetchNextPage: result.fetchNextPage,
      isLoading: result.isLoading,
      isPlaceholderData: result.isPlaceholderData,
      aggregations,
      isAggregationsLoading: aggregationsResult.isLoading,
      isAggregationsError: aggregationsResult.isError,
      error: result.error,
      isError: result.isError,
      total: result.data?.pages[0]?.total ?? 0,
      refetch: refetchQuery,
      reset: resetQuery,
      queryKey
    };
  };

  return {
    useFilteredEntitiesInfinite,
    useResetFilteredEntitiesInfinite
  };
};
